"use client";

import {
  AlarmClockCheck,
  CheckCircle2,
  Minus,
  Plus,
  XCircle,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Button } from "../../elements/button/button";
import { Input } from "../../elements/input/input";
import { Text } from "../../elements/text/text";
import { Tooltip } from "../../elements/tooltip/tooltip";
import { cn } from "../../lib/utils";
import { formatDateTime } from "../../utils/format-date";

const BIKE_MERCHANDISE_CATEGORY = "S0101";
const STOCK_LIMIT_BIKE = 20;
const STOCK_LIMIT_OTHERS = 50;

// Product-specific maximum orderable quantity limits
const MAX_ORDER_LIMIT_BIKE = 50;
const MAX_ORDER_LIMIT_OTHERS = 100;

// Keeping helpers here as it is used only in this file as of now, can be moved to utils if needed elsewhere
// Helpers: clamp number into [min,max]
function clampToRange(n: number, min = 0, max = 999): number {
  return Math.max(min, Math.min(max, n));
}

// Helpers: parse quantity input string into number or null
function parseQuantityInput(value: string, max = 999): number | null {
  if (value.trim() === "") {
    return null;
  }
  const n = Number(value);
  if (Number.isNaN(n)) {
    return null;
  }
  return Math.min(n, max);
}

// Helpers: format stock display based on stock and stockLimit
function formatStockDisplay(stock: number, stockLimit: number): string {
  if (stock <= stockLimit) {
    return String(stock);
  }
  return `${stockLimit}+`;
}

// Helpers: get real MAX limit
const getFinalStock = (locations: AvailabilityLocation[]): number => {
  let sum = 0;
  locations.forEach((location) => {
    if (location.stock !== undefined) {
      sum += location.stock;
    }
  });
  return sum;
};

// Availability status constants
const AVAILABILITY_STATUS = {
  AVAILABLE_NOW: "available-now",
  FUTURE_AVAILABILITY: "future-availability",
  OUT_OF_STOCK: "out-of-stock",
  NONE: "none",
} as const;

export type AvailabilityStatus =
  (typeof AVAILABILITY_STATUS)[keyof typeof AVAILABILITY_STATUS];

export type AvailabilityLocation = {
  name: string;
  status: AvailabilityStatus;
  itemCount?: number;
  stock?: number;
  stockLimit?: number;
  date?: string;
};

type State = {
  quantity: number;
  isFocused: boolean;
  prevPropsValue: number;
  isWarning: boolean;
};

export interface ProductInputQuantityProps {
  country?: string;
  value: number;
  disabled?: boolean;
  showSize?: boolean;
  showProductId?: boolean;
  showEanCode?: boolean;
  showAvailability?: boolean;
  availabilityIconsLayout?: "top" | "side";
  size?: string;
  productId: string;
  ean?: string;
  merchandiseCategory?: string;
  availability?: {
    status: AvailabilityStatus;
    amount?: number;
    locations?: AvailabilityLocation[];
  };
  onQuantityChange?: (quantity: number) => void;
  alertHandler?: (status: {
    isVisible: boolean;
    maxAvailability: number;
  }) => void;
  className?: string;
  min?: number;
  max?: number;
  effectiveLimit?: number;
  suffix?: string;
  microcopies: {
    quantity: string;
    increment: string;
    decrement: string;
    availableNow: string;
    futureAvailability: string;
    outOfStock: string;
    availabilityTitle?: string;
    itemsReadyForDelivery?: string;
  };
  onProductStateChange?: (productId: string, isModified: boolean) => void;
}

export function ProductInputQuantity(
  props: Readonly<ProductInputQuantityProps>
): ReactNode {
  const MAX = 999;
  const MIN_COUNT = props.min ?? 0;
  const MAX_COUNT = props.max ?? MAX;

  // Apply product-specific maximum order limits (client-side fallback only)
  const productMaxOrderLimit = props.merchandiseCategory?.startsWith(
    BIKE_MERCHANDISE_CATEGORY
  )
    ? MAX_ORDER_LIMIT_BIKE
    : MAX_ORDER_LIMIT_OTHERS;

  // Use backend-computed effectiveLimit as authoritative source
  // Backend already calculates: min(TotalAvailableStock, ProductTypeLimit)
  // Only fallback to client-side calculation if backend doesn't provide it
  let effectiveMax = MAX_COUNT;

  if (props.effectiveLimit !== undefined) {
    effectiveMax = props.effectiveLimit;
  } else if (
    props.availability?.locations &&
    props.availability.locations.length > 0
  ) {
    effectiveMax = Math.min(
      MAX_COUNT,
      getFinalStock(props.availability.locations),
      productMaxOrderLimit
    );
  }

  const [state, setState] = useState<State>({
    quantity: props.value,
    isFocused: false,
    prevPropsValue: props.value,
    isWarning: props.value === effectiveMax,
  });

  const inputActionDisabled =
    props.disabled ||
    props.availability?.status === AVAILABILITY_STATUS.OUT_OF_STOCK;

  // Sync with props.value changes when not focused (derived state pattern)
  if (props.value !== state.prevPropsValue && !state.isFocused) {
    setState({
      ...state,
      quantity: props.value,
      prevPropsValue: props.value,
    });
  }

  // Increment quantity by 1 (clamped to effectiveMax) and notify parent
  const handleIncrement = () => {
    const newValue = clampToRange(state.quantity + 1, MIN_COUNT, effectiveMax);
    // Reset focus when reaching effectiveMax to prevent focus ring from sticking
    setState((s) => ({
      ...s,
      quantity: newValue,
      isWarning: newValue === effectiveMax,
    }));
    if (newValue === effectiveMax) {
      props.alertHandler?.({ isVisible: true, maxAvailability: effectiveMax });
    } else {
      props.alertHandler?.({ isVisible: false, maxAvailability: effectiveMax });
    }
    props.onQuantityChange?.(newValue);
  };

  // Decrement quantity by 1 (clamped) and notify parent
  const handleDecrement = () => {
    const newValue = clampToRange(state.quantity - 1, MIN_COUNT, effectiveMax);
    // Reset focus when reaching MIN_COUNT to prevent focus ring from sticking
    setState((s) => ({
      ...s,
      quantity: newValue,
      isFocused: newValue <= MIN_COUNT ? false : s.isFocused,
      isWarning: newValue === effectiveMax,
    }));
    props.alertHandler?.({ isVisible: false, maxAvailability: effectiveMax });
    props.onQuantityChange?.(newValue);
  };

  // Parse, clamp and update quantity from input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (inputActionDisabled) {
      return;
    }

    let newValue = e.target.value;

    // Strip suffix if present
    if (props.suffix && newValue.endsWith(props.suffix)) {
      newValue = newValue.slice(0, -props.suffix.length).trim();
    }

    // When no suffix is provided, only allow whole numbers (no decimals)
    if (!props.suffix && newValue.includes(".")) {
      return;
    }

    // ✅ Normalize leading zeros (05 -> 5, 0007 -> 7, keep "0" as is)
    if (/^0+\d+/.test(newValue)) {
      newValue = String(Number.parseInt(newValue, 10));
      e.target.value = newValue;
    }

    // Handle empty string case (backspace to clear)
    if (newValue.trim() === "") {
      setState((s) => ({
        ...s,
        quantity: MIN_COUNT,
        isWarning: false,
      }));
      props.alertHandler?.({
        isVisible: false,
        maxAvailability: effectiveMax,
      });
      props.onQuantityChange?.(MIN_COUNT);
      return;
    }

    const numericValue = parseQuantityInput(newValue, effectiveMax);

    if (numericValue !== null) {
      const clampedValue = clampToRange(numericValue, MIN_COUNT, effectiveMax);

      setState((s) => ({
        ...s,
        quantity: clampedValue,
        isWarning: clampedValue === effectiveMax,
      }));

      props.alertHandler?.({
        isVisible: clampedValue === effectiveMax,
        maxAvailability: effectiveMax,
      });

      props.onQuantityChange?.(clampedValue);
    }
  };

  // Prevent unwanted keys (e.g., Enter, decimal point when no suffix) from submitting the input
  const blockUnwantedKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
    // Block decimal point when no suffix (whole numbers only)
    if (!props.suffix && (e.key === "." || e.key === ",")) {
      e.preventDefault();
    }
  };

  // Return availability icons based on locations - can show both available-now and future-availability
  const getAvailabilityIcons = () => {
    if (!props.availability?.locations) {
      return null;
    }

    const hasAvailableNow = props.availability.locations.some(
      (loc) => loc.status === AVAILABILITY_STATUS.AVAILABLE_NOW
    );
    const hasFutureAvailability = props.availability.locations.some(
      (loc) => loc.status === AVAILABILITY_STATUS.FUTURE_AVAILABILITY
    );

    const icons: ReactNode[] = [];
    if (hasAvailableNow) {
      icons.push(
        <span className="text-green-600" key="available-now">
          <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
        </span>
      );
    }
    if (hasFutureAvailability) {
      icons.push(
        <span className="text-amber-500" key="future-availability">
          <AlarmClockCheck aria-hidden="true" className="h-4 w-4" />
        </span>
      );
    }

    return icons.length > 0 ? icons : null;
  };

  // Return icon for out-of-stock status
  const getOutOfStockIcon = () => {
    if (props.availability?.status !== AVAILABILITY_STATUS.OUT_OF_STOCK) {
      return null;
    }
    return (
      <span className="text-red-600">
        <XCircle aria-hidden={true} className="h-4 w-4" />
      </span>
    );
  };

  const isDecrementDisabled =
    inputActionDisabled || state.quantity <= MIN_COUNT;
  const isIncrementDisabled =
    inputActionDisabled || state.quantity >= effectiveMax;

  let decrementOpacityClass = "opacity-0";
  if (isDecrementDisabled) {
    decrementOpacityClass = "opacity-0 invisible";
  } else if (state.isFocused) {
    decrementOpacityClass = "opacity-100";
  }

  let incrementOpacityClass = "opacity-0";
  if (isIncrementDisabled && inputActionDisabled) {
    incrementOpacityClass = "opacity-0 invisible";
  } else if (state.isFocused) {
    incrementOpacityClass = "opacity-100";
  }

  // Build tooltip content based on availability status
  const availabilityTooltipContent = (): ReactNode => {
    if (!props.availability) {
      return null;
    }

    const title = (
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold text-sm">
          {props.microcopies.availabilityTitle}
        </span>
      </div>
    );

    // Out of stock -> show Sold out message
    if (props.availability.status === AVAILABILITY_STATUS.OUT_OF_STOCK) {
      return (
        <div className="w-60 p-2">
          {title}
          <div className="mt-2 flex items-center gap-1 text-xs">
            <span className="text-red-600">
              <XCircle className="h-3 w-3" />
            </span>
            <span className="text-black">{props.microcopies.outOfStock}</span>
          </div>
        </div>
      );
    }

    // For available-now or future-availability, locations will always be present
    if (
      (props.availability.status === AVAILABILITY_STATUS.AVAILABLE_NOW ||
        props.availability.status ===
          AVAILABILITY_STATUS.FUTURE_AVAILABILITY) &&
      props.availability.locations &&
      props.availability.locations.length > 0
    ) {
      return (
        <div className="w-72 p-2">
          {title}
          <div className="space-y-3">
            {props.availability.locations.map((location) => (
              <div className="space-y-1" key={location.name}>
                <div className="font-semibold text-sm">{location.name}</div>
                <div className="flex items-center gap-1 text-xs">
                  {location.status === AVAILABILITY_STATUS.AVAILABLE_NOW && (
                    <span className="text-green-600">
                      <CheckCircle2 className="h-3 w-3" />
                    </span>
                  )}
                  {location.status ===
                    AVAILABILITY_STATUS.FUTURE_AVAILABILITY && (
                    <span className="text-amber-500">
                      <AlarmClockCheck className="h-3 w-3" />
                    </span>
                  )}

                  {/* Render per-location line:
                      - For available-now: show "{itemCount}+ {itemsReadyForDelivery}" when itemCount exists
                      - For future-availability: show the provided parts (count and/or date) joined by " | "
                      - If nothing provided, show empty string
                  */}
                  <span>
                    {(() => {
                      const stockLimit = props.merchandiseCategory?.startsWith(
                        BIKE_MERCHANDISE_CATEGORY
                      )
                        ? STOCK_LIMIT_BIKE
                        : STOCK_LIMIT_OTHERS;
                      if (
                        location.status === AVAILABILITY_STATUS.AVAILABLE_NOW
                      ) {
                        if (location.stock !== undefined) {
                          return `${formatStockDisplay(location.stock, stockLimit)} ${props.microcopies.itemsReadyForDelivery}`;
                        }
                        if (location.date) {
                          return formatDateTime(
                            new Date(location.date),
                            props?.country ?? "DE"
                          ).date;
                        }
                        return props.microcopies.availableNow;
                      }

                      // future-availability or other
                      const parts: string[] = [];
                      if (location.stock !== undefined) {
                        parts.push(
                          `${formatStockDisplay(location.stock, stockLimit)}`
                        );
                      }
                      if (location.date) {
                        parts.push(
                          formatDateTime(
                            new Date(location.date),
                            props.country ?? "DE"
                          ).date
                        );
                      }
                      return parts.join(" | ");
                    })()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // None status or no data - return null (no tooltip)
    return null;
  };

  const availabilityLayout = props.availabilityIconsLayout || "top";

  // Check if the current quantity differs from the original props value
  const isModified = state.quantity !== props.value;

  // notify parent of this product's modified state
  props.onProductStateChange?.(props.productId, isModified);

  // Render availability icons with tooltip
  const renderAvailabilityIcons = () => {
    if (!(props.showAvailability && props.availability)) {
      return null;
    }

    return (
      <div className="flex items-center gap-1">
        {/* Show out-of-stock icon without tooltip if out of stock */}
        {props.availability.status === AVAILABILITY_STATUS.OUT_OF_STOCK && (
          <Tooltip
            align="start"
            content={availabilityTooltipContent()}
            side="top"
          >
            {getOutOfStockIcon()}
          </Tooltip>
        )}
        {/* Show availability icons with tooltip for available-now or future-availability */}
        {(props.availability.status === AVAILABILITY_STATUS.AVAILABLE_NOW ||
          props.availability.status ===
            AVAILABILITY_STATUS.FUTURE_AVAILABILITY) && (
          <Tooltip
            align="start"
            content={availabilityTooltipContent()}
            side="top"
          >
            <div className="flex items-center gap-1">
              {getAvailabilityIcons()}
            </div>
          </Tooltip>
        )}
      </div>
    );
  };

  return (
    <div className={cn("flex flex-col gap-1", props.className)}>
      {/* Top row: Size and Availability (only when layout is top) */}
      {(props.showSize ||
        (props.showAvailability && availabilityLayout === "top")) && (
        <div className="flex items-center justify-between">
          {props.showSize && props.size && (
            <div className="font-semibold text-base">{props.size}</div>
          )}
          {availabilityLayout === "top" && renderAvailabilityIcons()}
        </div>
      )}

      {/* Quantity input with optional side availability icons */}
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "flex min-w-30 flex-1 items-center justify-center rounded border border-stone-300 bg-white",
            inputActionDisabled && "opacity-50",
            !state.isWarning && state.isFocused && "ring-4 ring-blue-300",
            isModified && !state.isFocused && "ring-4 ring-blue-300",
            state.isWarning &&
              state.isFocused &&
              "border-orange-700 ring-4 ring-orange-500"
          )}
        >
          <Button
            aria-label={props.microcopies.decrement}
            className={cn("shrink-0", decrementOpacityClass)}
            disabled={isDecrementDisabled || inputActionDisabled}
            onBlur={() => {
              setState((s) => ({ ...s, isFocused: false }));
              props.alertHandler?.({
                isVisible: false,
                maxAvailability: effectiveMax,
              });
            }}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              handleDecrement();
            }}
            onFocus={() => {
              setState((s) => ({ ...s, isFocused: true }));
              props.alertHandler?.({
                isVisible: state.isWarning,
                maxAvailability: effectiveMax,
              });
            }}
            size="icon-sm"
            tabIndex={-1}
            variant="ghost"
          >
            <Minus className="h-4 w-4" />
          </Button>

          <Input
            aria-label={props.microcopies.quantity}
            className="h-auto w-full min-w-13 border-0 text-center shadow-none focus-visible:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&[type='number']]:[-moz-appearance:textfield]"
            disabled={inputActionDisabled}
            max={effectiveMax}
            min={MIN_COUNT}
            name={props.productId}
            onBlur={() => {
              setState((s) => ({ ...s, isFocused: false }));
              props.alertHandler?.({
                isVisible: false,
                maxAvailability: effectiveMax,
              });
            }}
            onChange={handleInputChange}
            onClick={(e) => {
              if (inputActionDisabled) {
                e.preventDefault();
              }
            }}
            onFocus={() => {
              setState((s) => ({ ...s, isFocused: true }));
              props.alertHandler?.({
                isVisible: state.isWarning,
                maxAvailability: effectiveMax,
              });
            }}
            onKeyDown={(e) => {
              if (inputActionDisabled) {
                e.preventDefault();
                return;
              }
              blockUnwantedKeys(e);
            }}
            step={props.suffix ? undefined : "1"}
            type={props.suffix ? "text" : "number"}
            value={
              props.suffix ? `${state.quantity}${props.suffix}` : state.quantity
            }
          />

          <Button
            aria-label={props.microcopies.increment}
            className={cn("shrink-0", incrementOpacityClass)}
            disabled={inputActionDisabled}
            onBlur={() => {
              setState((s) => ({ ...s, isFocused: false }));
              props.alertHandler?.({
                isVisible: false,
                maxAvailability: effectiveMax,
              });
            }}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              handleIncrement();
            }}
            onFocus={() => setState((s) => ({ ...s, isFocused: true }))}
            size="icon-sm"
            tabIndex={-1}
            variant="ghost"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Availability icons on the side (when layout is side) */}
        {availabilityLayout === "side" && renderAvailabilityIcons()}
      </div>

      {/* Bottom row: Product ID/EAN and Availability text */}
      <div className="flex flex-col gap-1">
        {props.showProductId && (
          <Text variant="body-sm">
            {props.showEanCode && props.ean ? props.ean : props.productId}
          </Text>
        )}
      </div>
    </div>
  );
}
